/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { NpvrRecordingCapabilities, SourceType } from 'client-code/channels-service';
import { OfferPreview, RecordingAvailability } from 'client-code/previews';

import { MILLISECONDS_IN_A_SECOND } from '../../config';
import { Channel } from '../entities';
import { Locale, LocaleKeys } from '../locale';
import { TChannelV2, TProgramCell } from '../types';
import { VideoPlaybackController } from '../videoplayer/VideoPlaybackController';

type TRecordingRestriction = {
    isRestricted: boolean;
    message: string;
};

type TChannel = TChannelV2 | Channel | undefined;

export class RecordingRestriction {
    private static readonly TAG: string = 'RecordingRestriction';

    private static readonly DEFAULT_RR_DETAILS: TRecordingRestriction = {
        isRestricted: false,
        message: '',
    };

    public static readonly ACTION_STRIP: string = 'action-strip-restriction';

    public static readonly ACTION_STRIP_KEY_OK: string = 'action-strip-key-ok-restriction';

    public static readonly CHANNEL_CELL: string = 'channel-cell-restriction';

    public static readonly MESSAGE_SEPARATOR: string = '|';

    public static readonly OVERLAY_DIALOG: string = 'overlay-dialog-restriction';

    public static readonly PREVIEW_AREA: string = 'preview-area-restriction';

    public static readonly PROGRAM_CELL: string = 'program-cell-restriction';

    /* eslint-disable function-paren-newline */
    private static isChannelRestricted(channel: TChannel): boolean {
        if (channel) {
            const { policyInfo } = channel;
            const isRestricted: boolean =
                !policyInfo?.some((data) =>
                    data?.recordingRules?.restrictedCapability?.includes(
                        NpvrRecordingCapabilities.AllowRecording,
                    ),
                ) || false;
            return isRestricted;
        }

        return false;
    }

    private static isChannelSubscribed(channel: TChannel): boolean {
        return !!channel?.isSubscribed;
    }

    private static isOfferRestricted(preview: OfferPreview | undefined) {
        if (!preview) return false;

        const { recordingAvailability } = preview;
        return !(
            recordingAvailability === undefined ||
            recordingAvailability === RecordingAvailability.RecordingAllowed ||
            recordingAvailability === RecordingAvailability.RecordingAllowedAsNoChannelData
        );
    }

    private static isShowRestricted(channel: TChannel, programCell: TProgramCell): boolean {
        const { startTime, duration, isRecordable } = programCell;
        if (isRecordable !== undefined && isRecordable === false) return true;

        const calcEndTime = startTime.getTime() + duration * MILLISECONDS_IN_A_SECOND;
        const calcCurrentTime = Date.now();
        if (calcEndTime >= calcCurrentTime) return false;

        const RITPPolicy = channel?.policyInfo?.find(
            (data) => data?.recordingRules?.recordInThePastSeconds,
        );
        const recordInThePastSeconds = RITPPolicy?.recordingRules?.recordInThePastSeconds ?? 0;
        if (recordInThePastSeconds !== 0) {
            const recordAvailability =
                calcCurrentTime - recordInThePastSeconds * MILLISECONDS_IN_A_SECOND;
            if (calcEndTime <= recordAvailability) {
                return true;
            }
            return false;
        }

        return true;
    }

    private static evalActionStripRestriction(
        channel: TChannel,
        preview: OfferPreview | undefined,
    ): TRecordingRestriction {
        if (RecordingRestriction.isChannelRestricted(channel)) {
            return {
                isRestricted: true,
                message: `${
                    Locale.getString(LocaleKeys.RR_RECORDING_NOT_PERMITTED_TEXT) +
                    RecordingRestriction.MESSAGE_SEPARATOR +
                    Locale.getString(LocaleKeys.RR_CHANNEL_RESTRICTION_MESSAGE)
                }`,
            };
        }
        if (RecordingRestriction.isOfferRestricted(preview)) {
            return {
                isRestricted: true,
                message: `${
                    Locale.getString(LocaleKeys.RR_RECORDING_NOT_PERMITTED_TEXT) +
                    RecordingRestriction.MESSAGE_SEPARATOR +
                    Locale.getString(LocaleKeys.RR_COPYRIGHT_RESTRICTION_MESSAGE)
                }`,
            };
        }
        return RecordingRestriction.DEFAULT_RR_DETAILS;
    }

    private static evalChannelCellRestriction(channel: TChannel): TRecordingRestriction {
        if (RecordingRestriction.isChannelRestricted(channel)) {
            return { isRestricted: true, message: '' };
        }
        return RecordingRestriction.DEFAULT_RR_DETAILS;
    }

    private static evalOverlayDialogRestriction(
        channel: TChannel,
        programCell: TProgramCell | undefined,
    ): TRecordingRestriction {
        if (RecordingRestriction.isChannelRestricted(channel)) {
            return {
                isRestricted: true,
                message: Locale.getString(LocaleKeys.RR_CHANNEL_RESTRICTION_MESSAGE),
            };
        }

        if (programCell && RecordingRestriction.isShowRestricted(channel, programCell)) {
            return {
                isRestricted: true,
                message: Locale.getString(LocaleKeys.RR_COPYRIGHT_RESTRICTION_MESSAGE),
            };
        }

        return RecordingRestriction.DEFAULT_RR_DETAILS;
    }

    private static evalPreviewAreaRestriction(
        channel: TChannel,
        preview: OfferPreview | undefined,
    ): TRecordingRestriction {
        if (RecordingRestriction.isChannelRestricted(channel))
            return {
                isRestricted: true,
                message: Locale.getString(LocaleKeys.RR_CHANNEL_RESTRICTION_MESSAGE),
            };

        if (RecordingRestriction.isOfferRestricted(preview))
            return {
                isRestricted: true,
                message: Locale.getString(LocaleKeys.RR_COPYRIGHT_RESTRICTION_MESSAGE),
            };

        return RecordingRestriction.DEFAULT_RR_DETAILS;
    }

    private static evalProgramCellRestriction(
        channel: TChannel,
        programCell: TProgramCell | undefined,
    ): TRecordingRestriction {
        if (!RecordingRestriction.isChannelRestricted(channel) && programCell) {
            return {
                isRestricted: RecordingRestriction.isShowRestricted(channel, programCell),
                message: '',
            };
        }

        return RecordingRestriction.DEFAULT_RR_DETAILS;
    }

    /**
     * Returns restriction details object based on displayType and provided params.
     * @static
     * @param {string} displayType
     * @param {TChannel} [channel]
     * @param {TProgramCell} [programCell]
     * @param {OfferPreview} [preview]
     * @return {*}  {TRecordingRestriction}
     * @memberof RecordingRestriction
     */
    public static getRestrictionDetails(
        displayType: string,
        channel?: TChannel,
        programCell?: TProgramCell,
        preview?: OfferPreview,
    ): TRecordingRestriction {
        let restrictionDetails: TRecordingRestriction = RecordingRestriction.DEFAULT_RR_DETAILS;
        if (
            channel &&
            (!RecordingRestriction.isChannelSubscribed(channel) ||
                channel.sourceType === SourceType.JumpChannel)
        )
            return restrictionDetails;

        switch (displayType) {
            case RecordingRestriction.ACTION_STRIP:
                restrictionDetails = RecordingRestriction.evalActionStripRestriction(
                    channel,
                    preview,
                );
                break;

            case RecordingRestriction.ACTION_STRIP_KEY_OK:
                restrictionDetails = {
                    isRestricted: true,
                    message: Locale.getString(
                        RecordingRestriction.isChannelRestricted(
                            VideoPlaybackController.getInstance().getPlayable()?.channel,
                        )
                            ? LocaleKeys.RR_LIVE_TV_CHANNEL_NOTIFICATION_MESSAGE
                            : LocaleKeys.RR_LIVE_TV_NOTIFICATION_MESSAGE,
                    ),
                };
                break;

            case RecordingRestriction.CHANNEL_CELL:
                restrictionDetails = RecordingRestriction.evalChannelCellRestriction(channel);
                break;

            case RecordingRestriction.OVERLAY_DIALOG:
                restrictionDetails = RecordingRestriction.evalOverlayDialogRestriction(
                    channel,
                    programCell,
                );
                break;

            case RecordingRestriction.PREVIEW_AREA:
                restrictionDetails = RecordingRestriction.evalPreviewAreaRestriction(
                    channel,
                    preview,
                );
                break;

            case RecordingRestriction.PROGRAM_CELL:
                restrictionDetails = RecordingRestriction.evalProgramCellRestriction(
                    channel,
                    programCell,
                );
                break;

            default:
        }
        return restrictionDetails;
    }
}
